import React, { useEffect, useState } from "react";
import {
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Table,
    Button,
    Alert,
    Spinner,
    Badge
} from "reactstrap";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import Api from "../../components/helpers/Api";
import { SeoHelper } from "../../components/helpers/SeoHelper";
import { sessionEnded } from "../user/SessionEnded";

/**
 * Build the full campaign URL from a campaign record
 */
const buildCampaignUrl = (c) => {
    const params = new URLSearchParams();

    if (c.utmCampaign) params.append("utm_campaign", c.utmCampaign);
    if (c.utmSource) params.append("utm_source", c.utmSource);
    if (c.utmMedium) params.append("utm_medium", c.utmMedium);
    if (c.utmContent) params.append("utm_content", c.utmContent);
    if (c.utmTerm) params.append("utm_term", c.utmTerm);

    return `${c.baseUrl}?${params.toString()}`;
};

export default function QrCampaignList() {
    const navigate = useNavigate();

    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        const customer = sessionStorage.getItem("customer");
        if (!customer || !JSON.parse(customer).user?.isAdmin) {
            navigate("/");
            return;
        }

        Api.get("/api/admin/qr-campaigns")
            .then(res => setCampaigns(res.data || []))
            .catch(err => {
                console.error(err);
                setError("Failed to load Marketing campaigns");
            })
            .finally(() => setLoading(false));
    }, [navigate]);

    const deleteCampaign = async (campaign) => {
        const confirmed = window.confirm(
            `Delete QR campaign "${campaign.utmCampaign}"?\n\nThis cannot be undone.`
        );
        if (!confirmed) return;

        try {
            await Api.delete(`/api/admin/qr-campaigns/${campaign.id}`);
            setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
        } catch (err) {
            console.error(err);
            alert("Failed to delete campaign.");
        }
    };

    const downloadQr = (campaign) => {
        const wrapper = document.getElementById(`qr-${campaign.id}`);
        const qrCanvas = wrapper?.querySelector("canvas");
        if (!qrCanvas) return;

        const size = qrCanvas.width;
        const exportCanvas = document.createElement("canvas");
        exportCanvas.width = size;
        exportCanvas.height = size;

        const ctx = exportCanvas.getContext("2d");
        ctx.drawImage(qrCanvas, 0, 0);

        const logo = new Image();
        logo.src = "/qr/worship-bible-qr-logo.png";

        logo.onload = () => {
            const logoSize = size * 0.25;
            const x = (size - logoSize) / 2;
            const y = (size - logoSize) / 2;

            ctx.fillStyle = "#ffffff";
            ctx.fillRect(x - 6, y - 6, logoSize + 12, logoSize + 12);
            ctx.drawImage(logo, x, y, logoSize, logoSize);

            const link = document.createElement("a");
            link.download = `${campaign.utmCampaign || "qr-campaign"}.png`;
            link.href = exportCanvas.toDataURL("image/png");
            link.click();
        };
    };

    if (!sessionStorage.getItem("customer")) {
        return sessionEnded();
    }

    return (
        <>
            <SeoHelper noindex />

            <div className="position-relative">
                {/* HERO */}
                <section className="section section-lg section-shaped pb-250">
                    <div className="shape shape-style-1 shape-default">
                        <span /><span /><span /><span /><span /><span /><span /><span /><span />
                    </div>
                    <Container className="py-lg-md d-flex">
                        <div className="col px-0">
                            <Row>
                                <Col lg="8">
                                    <h1 className="display-3 text-white">
                                        Marketing tools<br />
                                        <span>Marketing campaigns</span>
                                    </h1>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                    <div className="separator separator-bottom separator-skew">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            preserveAspectRatio="none"
                            viewBox="0 0 2560 100"
                        >
                            <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                        </svg>
                    </div>
                </section>

                {/* MAIN PANEL */}
                <section className="section section-lg pt-lg-0 mt--200">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg="12">
                                <Card className="shadow border-0">
                                    <CardBody className="py-5">

                                        {/* NAV */}
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <Button
                                                color="link"
                                                className="p-0"
                                                onClick={() => navigate("/admin")}
                                            >
                                                &larr; Back to menu
                                            </Button>

                                            <Button
                                                color="primary"
                                                onClick={() => navigate("/admin/marketing/create")}
                                            >
                                                Create Campaign
                                            </Button>
                                        </div>

                                        {error && <Alert color="danger">{error}</Alert>}

                                        {loading ? (
                                            <div className="text-center py-5">
                                                <Spinner />
                                            </div>
                                        ) : campaigns.length === 0 ? (
                                            <Alert color="primary">
                                                No Marketing campaigns have been created yet.
                                            </Alert>
                                        ) : (
                                            <Table responsive hover>
                                                <thead>
                                                <tr>
                                                    <th>Campaign</th>
                                                    <th>Source / Medium</th>
                                                    <th>Created</th>
                                                    <th>By</th>
                                                    <th>Use</th>
                                                    <th />
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {campaigns.map(c => (
                                                    <React.Fragment key={c.id}>
                                                        <tr>
                                                            <td>
                                                                <strong>{c.utmCampaign}</strong>
                                                                <div className="small text-muted">
                                                                    {c.baseUrl}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <Badge color="primary" className="me-1 text-white">
                                                                    {c.utmSource}
                                                                </Badge>
                                                                &nbsp;/&nbsp;
                                                                <Badge color="secondary" className="ms-1">
                                                                    {c.utmMedium}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                {new Date(c.createdAt).toLocaleString()}
                                                            </td>
                                                            <td>
                                                                <small>{c.createdBy}</small>
                                                            </td>
                                                            <td>
                                                                <small>{c.intendedUse}</small>
                                                            </td>
                                                            <td className="text-end">
                                                                <Button
                                                                    size="sm"
                                                                    color="link"
                                                                    onClick={() =>
                                                                        setExpandedId(expandedId === c.id ? null : c.id)
                                                                    }
                                                                >
                                                                    {expandedId === c.id ? "Hide" : "View"}
                                                                </Button>
                                                            </td>
                                                        </tr>

                                                        {expandedId === c.id && (
                                                            <tr>
                                                                <td colSpan={6} className="bg-light">
                                                                    <div className="p-4 rounded border">
                                                                        <Row className="g-4">
                                                                            <Col md="3" className="text-center">
                                                                                <div className="bg-white p-3 rounded shadow-sm d-inline-block">
                                                                                    <div
                                                                                        id={`qr-${c.id}`}
                                                                                        style={{ position: "relative" }}
                                                                                    >
                                                                                        <QRCodeCanvas
                                                                                            value={buildCampaignUrl(c)}
                                                                                            size={170}
                                                                                            level="H"
                                                                                            includeMargin
                                                                                            fgColor="#5E72E4"
                                                                                            bgColor="#ffffff"
                                                                                        />
                                                                                        <img
                                                                                            src="/qr/worship-bible-qr-logo.png"
                                                                                            alt="Worship Bible"
                                                                                            style={{
                                                                                                position: "absolute",
                                                                                                top: "50%",
                                                                                                left: "50%",
                                                                                                width: 42,
                                                                                                height: 42,
                                                                                                transform: "translate(-50%, -50%)",
                                                                                                background: "white",
                                                                                                padding: 4,
                                                                                                borderRadius: 6
                                                                                            }}
                                                                                        />
                                                                                    </div>

                                                                                    <Button
                                                                                        outline
                                                                                        size="sm"
                                                                                        color="primary"
                                                                                        className="mt-3"
                                                                                        onClick={() => downloadQr(c)}
                                                                                    >
                                                                                        Download QR
                                                                                    </Button>
                                                                                </div>
                                                                            </Col>

                                                                            <Col md="9">
                                                                                <div className="mb-3">
                                                                                    <div
                                                                                        className="small text-muted mb-1">
                                                                                        Full URL
                                                                                    </div>
                                                                                    <code
                                                                                        className="d-block small text-break bg-white p-2 rounded border text-primary">
                                                                                        {buildCampaignUrl(c)}
                                                                                    </code>
                                                                                </div>

                                                                                <Row className="mb-3">
                                                                                    <Col sm="6">
                                                                                        <div
                                                                                            className="small text-muted">Created
                                                                                            by
                                                                                        </div>
                                                                                        <div
                                                                                            className="small">{c.createdBy}</div>
                                                                                    </Col>
                                                                                    <Col sm="6">
                                                                                        <div
                                                                                            className="small text-muted">Created
                                                                                        </div>
                                                                                        <div className="small">
                                                                                            {new Date(c.createdAt).toLocaleString()}
                                                                                        </div>
                                                                                    </Col>
                                                                                </Row>

                                                                                {c.notes && (
                                                                                    <>
                                                                                        <div
                                                                                            className="small text-muted mb-1">
                                                                                            Notes
                                                                                        </div>
                                                                                        <div className="small">
                                                                                            {c.notes}
                                                                                        </div>
                                                                                    </>
                                                                                )}

                                                                                <div className="d-flex gap-2">
                                                                                    <Button
                                                                                        size="sm"
                                                                                        color="secondary"
                                                                                        outline
                                                                                        onClick={() => navigate(`/admin/marketing/edit/${c.id}`)}
                                                                                    >
                                                                                        Edit
                                                                                    </Button>

                                                                                    <Button
                                                                                        size="sm"
                                                                                        color="danger"
                                                                                        outline
                                                                                        onClick={() => deleteCampaign(c)}
                                                                                    >
                                                                                        Delete
                                                                                    </Button>
                                                                                </div>
                                                                            </Col>
                                                                        </Row>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                                </tbody>
                                            </Table>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </div>
        </>
    );
}